import { useState, useCallback, useEffect, useMemo } from 'react';
import { Alert, CircularProgress } from '@mui/material';
import {
  GlobalButton,
  AutocompleteInput,
  DateInput,
  SelectMultiInput,
} from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import {
  Search as SearchIcon,
  DeleteForever as TrashIcon,
  FileCopy as ExportIcon,
} from '@mui/icons-material';
import { Option } from '@/components/inputs/AutocompleteInput';
import {
  useTransitControlCommercesAutocomplete,
  useTransitControlStatusesAutocomplete,
  useTransitControlServiceWithOperatorsOptions,
  useTransitControlExport,
} from '../hooks';
import useLogisticOperatorAutocomplete from '@/pages/Business/pages/LogisticOperator/hooks/useLogisticOperatorAutocomplete';
import { useGetCommerceByIdQuery } from '@/pages/Business/pages/Commerce/services/commerces.service';
import { useGetLogisticOperatorByIdQuery } from '@/pages/Business/pages/LogisticOperator/services';
import { useGetShippingStatusByIdQuery } from '@/pages/Business/pages/LogisticOperator/services/shipping-statuses.service';
import { startOfLocalDay, endOfLocalDay } from '@/utils/dates';
import { resolveSelectedOptionLabel } from '@/utils/selected-option-label';
import { TransitControlFilter } from '../models';

interface TransitControlSearchBarProps {
  initialFilter: TransitControlFilter;
  filter: TransitControlFilter;
  search: (updatedFilter: TransitControlFilter) => void;
  isLoading?: boolean;
}

function getOptionId(option: Option | null): string {
  const data = option?.data;
  if (!data) return '';
  if (typeof data === 'string') return data;
  if (typeof data === 'object' && 'id' in data) {
    return (data as { id: string }).id ?? '';
  }
  return '';
}

const TransitControlSearchBar = ({
  initialFilter,
  filter,
  search,
  isLoading = false,
}: TransitControlSearchBarProps) => {
  const [localFilter, setLocalFilter] = useState<TransitControlFilter>(filter);
  const [originStatusInput, setOriginStatusInput] = useState('');
  const [destinationStatusInput, setDestinationStatusInput] = useState('');
  const [commerceInput, setCommerceInput] = useState('');
  const [logisticOperatorInput, setLogisticOperatorInput] = useState('');

  useEffect(() => {
    setLocalFilter(filter);
    setOriginStatusInput('');
    setDestinationStatusInput('');
    setCommerceInput('');
    setLogisticOperatorInput('');
  }, [filter]);

  const { shippingStatusOptions: originStatusOptions, isShippingStatusesLoading: isOriginStatusesLoading } =
    useTransitControlStatusesAutocomplete({ search: originStatusInput, minLength: 3 });
  const { shippingStatusOptions: destinationStatusOptions, isShippingStatusesLoading: isDestinationStatusesLoading } =
    useTransitControlStatusesAutocomplete({ search: destinationStatusInput, minLength: 3 });
  const { commerceOptions, isCommercesLoading } = useTransitControlCommercesAutocomplete({
    search: commerceInput,
    minLength: 3,
  });
  const { logisticOperatorOptions, isLogisticOperatorsLoading } = useLogisticOperatorAutocomplete({
    search: logisticOperatorInput,
    minLength: 3,
  });
  const {
    serviceWithOperatorOptions,
    isLoading: isServiceWithOperatorsLoading,
    requiresLogisticOperator,
  } = useTransitControlServiceWithOperatorsOptions({
    logisticOperatorId: localFilter.logisticOperatorId,
  });

  const {
    data: originStatusDetails,
    isFetching: isOriginStatusDetailsFetching,
  } = useGetShippingStatusByIdQuery(
    { shippingStatusId: localFilter.originStatusId },
    { skip: !localFilter.originStatusId }
  );
  const {
    data: destinationStatusDetails,
    isFetching: isDestinationStatusDetailsFetching,
  } = useGetShippingStatusByIdQuery(
    { shippingStatusId: localFilter.destinationStatusId },
    { skip: !localFilter.destinationStatusId }
  );
  const { data: commerceDetails, isFetching: isCommerceDetailsFetching } =
    useGetCommerceByIdQuery(
    { commerceId: localFilter.commerceId },
    { skip: !localFilter.commerceId }
  );
  const {
    data: logisticOperatorDetails,
    isFetching: isLogisticOperatorDetailsFetching,
  } = useGetLogisticOperatorByIdQuery(
    { logisticOperatorId: localFilter.logisticOperatorId },
    { skip: !localFilter.logisticOperatorId }
  );

  const selectedOriginStatus = useMemo((): Option | null => {
    if (!localFilter.originStatusId) return null;
    const label = resolveSelectedOptionLabel(
      originStatusDetails?.commercialName || originStatusDetails?.name,
      {
        isResolving: isOriginStatusDetailsFetching,
        fallback: 'Estado seleccionado',
      }
    );
    return { label, data: localFilter.originStatusId };
  }, [
    localFilter.originStatusId,
    originStatusDetails,
    isOriginStatusDetailsFetching,
  ]);

  const selectedDestinationStatus = useMemo((): Option | null => {
    if (!localFilter.destinationStatusId) return null;
    const label = resolveSelectedOptionLabel(
      destinationStatusDetails?.commercialName ||
        destinationStatusDetails?.name,
      {
        isResolving: isDestinationStatusDetailsFetching,
        fallback: 'Estado seleccionado',
      }
    );
    return { label, data: localFilter.destinationStatusId };
  }, [
    localFilter.destinationStatusId,
    destinationStatusDetails,
    isDestinationStatusDetailsFetching,
  ]);

  const selectedCommerce = useMemo((): Option | null => {
    if (!localFilter.commerceId) return null;
    return {
      label: resolveSelectedOptionLabel(commerceDetails?.name, {
        isResolving: isCommerceDetailsFetching,
        fallback: 'Comercio seleccionado',
      }),
      data: localFilter.commerceId,
    };
  }, [
    localFilter.commerceId,
    commerceDetails?.name,
    isCommerceDetailsFetching,
  ]);

  const selectedLogisticOperator = useMemo((): Option | null => {
    if (!localFilter.logisticOperatorId) return null;
    return {
      label: resolveSelectedOptionLabel(logisticOperatorDetails?.fullName, {
        isResolving: isLogisticOperatorDetailsFetching,
        fallback: 'Operador seleccionado',
      }),
      data: localFilter.logisticOperatorId,
    };
  }, [
    localFilter.logisticOperatorId,
    logisticOperatorDetails,
    isLogisticOperatorDetailsFetching,
  ]);

  const handleUpdateFilter = useCallback((updatedFilter: Partial<TransitControlFilter>) => {
    setLocalFilter((prev) => ({ ...prev, ...updatedFilter }));
  }, []);

  const handleOriginStatusSelect = useCallback((option: Option | null) => {
    handleUpdateFilter({ originStatusId: getOptionId(option) });
  }, [handleUpdateFilter]);

  const handleDestinationStatusSelect = useCallback((option: Option | null) => {
    handleUpdateFilter({ destinationStatusId: getOptionId(option) });
  }, [handleUpdateFilter]);

  const handleCommerceSelect = useCallback((option: Option | null) => {
    handleUpdateFilter({ commerceId: getOptionId(option) });
  }, [handleUpdateFilter]);

  const handleLogisticOperatorSelect = useCallback((option: Option | null) => {
    handleUpdateFilter({ logisticOperatorId: getOptionId(option), serviceWithOperatorIds: [] });
  }, [handleUpdateFilter]);

  const resetFilter = () => {
    setLocalFilter(initialFilter);
    setOriginStatusInput('');
    setDestinationStatusInput('');
    setCommerceInput('');
    setLogisticOperatorInput('');
    search(initialFilter);
  };

  const handleSearch = () => {
    search(localFilter);
  };

  const hasFilterChanges = useMemo(() => {
    const normalize = (value: TransitControlFilter) => {
      const keys = Object.keys(value).sort();
      return JSON.stringify(value, keys);
    };
    return normalize(localFilter) !== normalize(filter);
  }, [localFilter, filter]);

  const { handleExport, isExporting, getExportValidationMessage } = useTransitControlExport();
  const exportValidationMessage = getExportValidationMessage(localFilter);

  return (
    <div className='w-full p-5 flex flex-col justify-center bg-white rounded-sm shadow-xl gap-5'>
      <div className='grid grid-cols-3 gap-4'>
        <div className='bg-gray-50 p-4 rounded-lg'>
          <h3 className='text-lg font-semibold text-gray-700 mb-4'>Estados (obligatorio)</h3>
          <div className='flex flex-col gap-4'>
            <AutocompleteInput
              name='origin-status-search'
              placeholder='Estado Origen'
              value={selectedOriginStatus}
              inputValue={selectedOriginStatus ? selectedOriginStatus.label : originStatusInput}
              onChange={handleOriginStatusSelect}
              onInputChange={setOriginStatusInput}
              options={originStatusOptions}
              required
              loading={isOriginStatusesLoading}
            />
            <AutocompleteInput
              name='destination-status-search'
              placeholder='Estado Destino Esperado'
              value={selectedDestinationStatus}
              inputValue={
                selectedDestinationStatus ? selectedDestinationStatus.label : destinationStatusInput
              }
              onChange={handleDestinationStatusSelect}
              onInputChange={setDestinationStatusInput}
              options={destinationStatusOptions}
              required
              loading={isDestinationStatusesLoading}
            />
          </div>
        </div>

        <div className='bg-gray-50 p-4 rounded-lg'>
          <h3 className='text-lg font-semibold text-gray-700 mb-4'>Comercio y operador</h3>
          <div className='flex flex-col gap-4'>
            <AutocompleteInput
              name='commerce-transit-control-search'
              placeholder='Comercio'
              value={selectedCommerce}
              inputValue={selectedCommerce ? selectedCommerce.label : commerceInput}
              onChange={handleCommerceSelect}
              onInputChange={setCommerceInput}
              options={commerceOptions}
              required={false}
              loading={isCommercesLoading}
            />
            <AutocompleteInput
              name='logistic-operator-transit-control-search'
              placeholder='Operador logístico'
              value={selectedLogisticOperator}
              inputValue={
                selectedLogisticOperator ? selectedLogisticOperator.label : logisticOperatorInput
              }
              onChange={handleLogisticOperatorSelect}
              onInputChange={setLogisticOperatorInput}
              options={logisticOperatorOptions}
              required={false}
              loading={isLogisticOperatorsLoading}
            />
            <SelectMultiInput
              name='service-with-operator-transit-control-search'
              placeholder='Servicio con operador'
              value={localFilter.serviceWithOperatorIds || []}
              onChange={(value) => handleUpdateFilter({ serviceWithOperatorIds: value })}
              options={serviceWithOperatorOptions}
              disabled={requiresLogisticOperator || isServiceWithOperatorsLoading}
            />
          </div>
        </div>

        <div className='bg-gray-50 p-4 rounded-lg'>
          <h3 className='text-lg font-semibold text-gray-700 mb-4'>
            Rango de fechas (máx. 20 días, obligatorio)
          </h3>
          <div className='grid grid-cols-2 gap-3'>
            <DateInput
              name='transit-control-date-from'
              placeholder='Desde'
              value={localFilter.dateFrom || null}
              clearable
              onChange={(date) => handleUpdateFilter({ dateFrom: startOfLocalDay(date) })}
            />
            <DateInput
              name='transit-control-date-to'
              placeholder='Hasta'
              value={localFilter.dateTo || null}
              clearable
              onChange={(date) => handleUpdateFilter({ dateTo: endOfLocalDay(date) })}
            />
          </div>
        </div>
      </div>

      <div className='flex gap-3 justify-end'>
        <GlobalButton
          variant={GlobalButtonVariant.secondaryButton}
          action={resetFilter}
          disabled={isExporting}
        >
          <TrashIcon color='primary' />
          Limpiar
        </GlobalButton>
        <GlobalButton
          variant={GlobalButtonVariant.secondaryButton}
          action={() => handleExport(localFilter)}
          disabled={isExporting || !!exportValidationMessage}
        >
          {isExporting ? <CircularProgress size={20} color='inherit' /> : <ExportIcon color='primary' />}
          Exportar
        </GlobalButton>
        <GlobalButton
          variant={GlobalButtonVariant.primaryButton}
          action={handleSearch}
          disabled={isExporting || isLoading || !hasFilterChanges || !!exportValidationMessage}
        >
          {isLoading ? <CircularProgress size={20} color='inherit' /> : <SearchIcon />}
          {isLoading ? 'Buscando...' : 'Buscar'}
        </GlobalButton>
      </div>
      {exportValidationMessage ? (
        <Alert severity='warning'>{exportValidationMessage}</Alert>
      ) : null}
    </div>
  );
};

export default TransitControlSearchBar;
